import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Download, Key, Code, CheckCircle, Copy, Check, ExternalLink,
  ShoppingBag, Eye, Zap, Shield, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsProofIntegration() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const snippetCode = `<!-- Commerce Index Proof Widget -->
<script>
  (function(w, d, s, o, f, js, fjs) {
    w['CIProof'] = o;
    w[o] = w[o] || function() { (w[o].q = w[o].q || []).push(arguments) };
    js = d.createElement(s); fjs = d.getElementsByTagName(s)[0];
    js.id = o; js.src = f; js.async = 1;
    fjs.parentNode.insertBefore(js, fjs);
  }(window, document, 'script', 'ciproof', 'https://cdn.commerceindex.com/proof.js'));

  ciproof('init', {
    brandId: 'YOUR_BRAND_ID',
    publicKey: 'YOUR_PUBLIC_KEY'
  });
</script>`;

  const trackViewCode = `ciproof('trackView', {
  productId: 'SKU-12345',
  productTitle: 'Red Performance Legging',
  productPrice: 74.99,
  productCategory: 'Activewear'
});`;

  const trackPurchaseCode = `ciproof('trackPurchase', {
  orderId: 'ORD-98765',
  items: [
    {
      productId: 'SKU-12345',
      title: 'Red Performance Legging',
      quantity: 2,
      unitPrice: 74.99
    }
  ],
  totalValue: 149.98,
  currency: 'USD'
});`;

  const showProofCode = `ciproof('showProof', {
  productId: 'SKU-12345',
  container: '#ci-proof-widget',
  theme: 'light',
  position: 'inline'
});`;

  const curlViewCode = `curl -X POST https://commerceindex.com/api/collect/product-view \\
  -H "Content-Type: application/json" \\
  -H "X-CI-Api-Key: YOUR_API_KEY" \\
  -d '{
    "brand_id": "brand_yourcompany",
    "product_id": "SKU-12345",
    "product_title": "Red Performance Legging",
    "session_id": "sess_abc123"
  }'`;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          <Eye className="w-3 h-3 mr-1" />
          Integration Guide
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          CI Proof Widget Integration
        </h1>
        <p className="text-[#94A3B8] text-lg">
          Add real-time social proof to your product pages. Show actual views and purchases, not fake counters.
        </p>
      </div>

      {/* Request API Keys Section */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-[#FFD166]" />
          Step 1: Request API Keys
        </h2>
        
        <Card className="bg-[#0D1628] border-[#22304A] mb-6">
          <CardContent className="pt-6">
            <p className="text-[#94A3B8] mb-4">
              To integrate CI Proof, you will need API credentials from Commerce Index.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#3EF1C7]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#3EF1C7] font-bold">1</span>
                </div>
                <div>
                  <h4 className="text-white font-medium">Visit the CI Proof Page</h4>
                  <p className="text-[#94A3B8] text-sm">Go to <a href="/proof" className="text-[#3EF1C7] hover:underline">/proof</a> and click &ldquo;Talk to Us About Activating Proof&rdquo;</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#3EF1C7]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#3EF1C7] font-bold">2</span>
                </div>
                <div>
                  <h4 className="text-white font-medium">Submit Your Information</h4>
                  <p className="text-[#94A3B8] text-sm">Company name, website URL, estimated monthly page views, contact email</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#3EF1C7]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#3EF1C7] font-bold">3</span>
                </div>
                <div>
                  <h4 className="text-white font-medium">Receive Your Credentials</h4>
                  <p className="text-[#94A3B8] text-sm">Within 1-2 business days, you will receive:</p>
                  <ul className="text-[#94A3B8] text-sm mt-2 space-y-1">
                    <li><code className="bg-[#1E293B] px-2 py-0.5 rounded">brand_id</code> - Your unique brand identifier</li>
                    <li><code className="bg-[#1E293B] px-2 py-0.5 rounded">api_key</code> - API key for server-side calls</li>
                    <li><code className="bg-[#1E293B] px-2 py-0.5 rounded">public_key</code> - Public key for client-side snippet</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <Button 
                className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90"
                onClick={() => window.open('/proof', '_blank')}
              >
                Request API Keys
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Integration Options */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Code className="w-5 h-5 text-[#7A5CFF]" />
          Step 2: Choose Integration Method
        </h2>
        
        <Tabs defaultValue="javascript" className="w-full">
          <TabsList className="bg-[#1E293B] border-[#334155]">
            <TabsTrigger value="javascript" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
              JavaScript Snippet
            </TabsTrigger>
            <TabsTrigger value="rest" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
              REST API
            </TabsTrigger>
            <TabsTrigger value="shopify" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
              Shopify
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="javascript" className="mt-4">
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <CardTitle className="text-white text-lg">JavaScript Snippet (Recommended)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="text-white font-medium mb-2">1. Add the Snippet</h4>
                  <p className="text-[#94A3B8] text-sm mb-2">Add this to your product pages, before the closing &lt;/body&gt; tag:</p>
                  <div className="relative">
                    <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm">
                      <code className="text-[#E2E8F0]">{snippetCode}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(snippetCode, 'snippet')}
                      className="absolute top-2 right-2 p-2 bg-[#334155] rounded hover:bg-[#475569] transition-colors"
                    >
                      {copiedCode === 'snippet' ? <Check className="w-4 h-4 text-[#3EF1C7]" /> : <Copy className="w-4 h-4 text-[#94A3B8]" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">2. Track Product Views</h4>
                  <div className="relative">
                    <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm">
                      <code className="text-[#E2E8F0]">{trackViewCode}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(trackViewCode, 'trackView')}
                      className="absolute top-2 right-2 p-2 bg-[#334155] rounded hover:bg-[#475569] transition-colors"
                    >
                      {copiedCode === 'trackView' ? <Check className="w-4 h-4 text-[#3EF1C7]" /> : <Copy className="w-4 h-4 text-[#94A3B8]" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">3. Track Purchases</h4>
                  <div className="relative">
                    <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm">
                      <code className="text-[#E2E8F0]">{trackPurchaseCode}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(trackPurchaseCode, 'trackPurchase')}
                      className="absolute top-2 right-2 p-2 bg-[#334155] rounded hover:bg-[#475569] transition-colors"
                    >
                      {copiedCode === 'trackPurchase' ? <Check className="w-4 h-4 text-[#3EF1C7]" /> : <Copy className="w-4 h-4 text-[#94A3B8]" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">4. Display Widget</h4>
                  <div className="relative">
                    <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm">
                      <code className="text-[#E2E8F0]">{showProofCode}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(showProofCode, 'showProof')}
                      className="absolute top-2 right-2 p-2 bg-[#334155] rounded hover:bg-[#475569] transition-colors"
                    >
                      {copiedCode === 'showProof' ? <Check className="w-4 h-4 text-[#3EF1C7]" /> : <Copy className="w-4 h-4 text-[#94A3B8]" />}
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="rest" className="mt-4">
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <CardTitle className="text-white text-lg">REST API (Server-Side)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] mb-4">
                  For server-side tracking or custom implementations, use our REST API.
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-white font-medium mb-2">Track Product View</h4>
                    <div className="relative">
                      <pre className="bg-[#1E293B] p-4 rounded-lg overflow-x-auto text-sm">
                        <code className="text-[#E2E8F0]">{curlViewCode}</code>
                      </pre>
                      <button
                        onClick={() => copyToClipboard(curlViewCode, 'curlView')}
                        className="absolute top-2 right-2 p-2 bg-[#334155] rounded hover:bg-[#475569] transition-colors"
                      >
                        {copiedCode === 'curlView' ? <Check className="w-4 h-4 text-[#3EF1C7]" /> : <Copy className="w-4 h-4 text-[#94A3B8]" />}
                      </button>
                    </div>
                  </div>
                  <p className="text-[#94A3B8] text-sm">
                    See the <a href="/docs/rest-api" className="text-[#3EF1C7] hover:underline">REST API documentation</a> for all endpoints.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="shopify" className="mt-4">
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader>
                <CardTitle className="text-white text-lg">Shopify Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                    <p className="text-[#94A3B8]">Install the Commerce Index app from the Shopify App Store</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                    <p className="text-[#94A3B8]">Connect your store in the app settings</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                    <p className="text-[#94A3B8]">Widget automatically appears on product pages</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                    <p className="text-[#94A3B8]">Configure appearance in the Merchant Portal</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </section>

      {/* What You Get */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#3EF1C7]" />
          What You Get with CI Proof
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <Eye className="w-8 h-8 text-[#3EF1C7] mb-3" />
              <h4 className="text-white font-medium mb-2">Social Proof Widget</h4>
              <p className="text-[#94A3B8] text-sm">Display real-time views and purchases on your PDPs</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <ShoppingBag className="w-8 h-8 text-[#7A5CFF] mb-3" />
              <h4 className="text-white font-medium mb-2">CI Score Visibility</h4>
              <p className="text-[#94A3B8] text-sm">Access CI Score for your brand and products</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <Zap className="w-8 h-8 text-[#FFD166] mb-3" />
              <h4 className="text-white font-medium mb-2">Signals API Access</h4>
              <p className="text-[#94A3B8] text-sm">Brand and product signals for your dashboards</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="pt-6">
              <Shield className="w-8 h-8 text-[#3EF1C7] mb-3" />
              <h4 className="text-white font-medium mb-2">Privacy-First</h4>
              <p className="text-[#94A3B8] text-sm">No personal tracking, GDPR/CCPA compliant</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Support */}
      <section className="mb-8">
        <Card className="bg-[#7A5CFF]/10 border-[#7A5CFF]/30">
          <CardContent className="pt-6">
            <h3 className="text-white font-semibold mb-2">Need Help?</h3>
            <p className="text-[#94A3B8] text-sm mb-4">
              Our team is here to help you integrate CI Proof successfully.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button variant="outline" className="border-[#7A5CFF] text-[#7A5CFF] hover:bg-[#7A5CFF]/20">
                <a href="/contact">Contact Support</a>
              </Button>
              <Button variant="outline" className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B]">
                <a href="/docs/rest-api">View Full API Docs</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
